import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';
import {
  CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { Input } from '../../common/Input';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';

// icons
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';

function ClientIdentity({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Client Name",
    "Status",
    "Actions"
  ]

  var accessors = [
    "id",
    "clientName",
    "isActive",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [filterData, setFilterData] = useState([]);
  
  // Form state - only clientName and status
  const [form, setForm] = useState({
    clientName: "",
    isActive: true,
  });

  // Add/Edit form visibility
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Check if data already exists
  const [hasExistingData, setHasExistingData] = useState(false);

  const apiUrl = domain + "/api/ClientIdentity";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log("ClientIdentity data: ", response.data);
      setData(response.data);
      setFilterData(response.data);
      
      // Check if there's existing data
      setHasExistingData(response.data && response.data.length > 0);
    } catch (error) {
      console.error('Error fetching client identity data:', error);
      showGeneralMessage("Failed to fetch client identity data", false);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, showGeneralMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetLocalVariables = () => {
    setForm({
      clientName: "",
      isActive: true,
    });
    setSelectedData(null);
  };

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    const filtered = filterData.filter((f) =>
      f?.clientName?.toString().toLowerCase().includes(lowercaseValue)
    );
    setData(filtered);
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();

    // Check if data already exists
    if (hasExistingData) {
      showGeneralMessage("Cannot create new entry. Only one client identity record is allowed. Please edit the existing record instead.", false);
      closeAddHandleClick();
      return;
    }

    // Validate form
    if (!form.clientName.trim()) {
      showGeneralMessage("Please fill in client name field", false);
      return;
    }

    // Prepare data for API - only clientName field
    const apiData = {
      clientName: form.clientName.trim(),
      isActive: form.isActive, // This will be true or false based on checkbox state
    };

    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();

      showGeneralMessage("Client identity created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create client identity";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdateSubmit = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    // Validate form
    if (!form.clientName.trim()) {
      showGeneralMessage("Please fill in client name field", false);
      return;
    }

    // Prepare data for API - only clientName field
    const apiData = {
      clientName: form.clientName.trim(),
      isActive: form.isActive, // This will be true or false based on checkbox state
    };

    console.log('Updating data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Client identity updated successfully!", true);
      resetLocalVariables();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update client identity";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      clientName: data.clientName || "",
      isActive: data.isActive, // Directly use the value from data
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Dev Mode > "} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Client"}</span>
              </div>
              <h3>Client Identity</h3>
            </div>
            <div className='p-4 space-y-4'>
              <SearchBar handleFilter={handleFilter} />

              {/* Only show Add button if no existing data */}
              {!hasExistingData && (
                <Button
                  icon={<AddIcon />}
                  name={"Add Client Identity"}
                  className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                  onClick={openAddHandleClick}
                />
              )}

              <CreateTable
                tableName={"ClientIdentity"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Form - Only show if no existing data */}
      {!hasExistingData && (
        <AddEditForm
          isOpen={showAddForm}
          onClose={closeAddHandleClick}
          form={form}
          setForm={setForm}
          handleSubmit={handleAddSubmit}
          modalName={"Add Client Identity"}
          mode="add"
        />
      )}

      {/* Edit Form */}
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdateSubmit}
        modalName={"Update Client Identity"}
        mode="edit"
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading} />
    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
}) {

  const submit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleClose = () => {
    onClose();
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle checkbox change specifically for isActive
  const handleCheckboxChange = (field, checked) => {
    setForm(prev => ({
      ...prev,
      [field]: checked // This will be true or false directly
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[40%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">
            
            {/* Client Name Section */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Client Identity Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Input
                  label="Client Name:"
                  value={form.clientName}
                  onChange={(e) => handleInputChange('clientName', e.target.value)}
                  placeholder="Enter client name (e.g., 'ABC Corporation', 'John Doe', 'XYZ Partners')"
                  isRequired={true}
                />

                {/* Status Checkbox */}
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={form.isActive} // Direct boolean value
                    onChange={(e) => handleCheckboxChange('isActive', e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="isActive" className="text-gray-700 font-medium">
                    Active
                  </label>
                  <span className="text-sm text-gray-500 ml-2">
                    {form.isActive ? "(Will be saved as active)" : "(Will be saved as inactive)"}
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white shadow-sm rounded-b-lg">
            <button
              onClick={handleClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

export default ClientIdentity;